export interface LookupItem {
  id: number;
  title: string;
}

export interface Transaction {
  id: number;
  title: string;
  description?: string;
  date: string;
  projectId: number;
  projectTitle: string;
  customerTitle: string;
  amount: number;
  purchaseIndex: number;
  exchangeRate: number;
  totalAmount: number;
  currencyId: number;
  currencyName: string;
  typeId: number;
  isCompleted: boolean;
  directionId: number;
  category?: number;
  sourceId?: number;
  isProcessed: boolean;
  createdDate?: string;
  updatedDate?: string;
}

export interface CreateTransaction {
  title: string;
  description?: string;
  date: string;
  projectId: number;
  amount: number;
  purchaseIndex: number;
  exchangeRate: number;
  currencyId: number;
  typeId: number;
  isCompleted: boolean;
  directionId: number;
  category?: number;
  sourceId?: number;
  isProcessed: boolean;
}

export interface UpdateTransaction extends CreateTransaction {
  id: number;
}

export interface TransactionFilter {
  category?: number;
  customerId?: number;
  directionId?: number;
  sourceId?: number;
  currencyId?: number;
  transactionTypeId?: number;
  projectId?: number;
  startDate?: string;
  endDate?: string;
  searchText?: string;
  skip?: number;
  take?: number;
}

export interface Dashboard {
  yearTotalInUsd: number;
  yearTotalInUsdIncludingNotPaid: number;
  dashboards: Quarter[];
  transactionsNotCompleted: Transaction[];
  totalUnpaidAmount: number;
  topCustomersWithDebt: CustomerDebt[];
  duplicateTransactions: Transaction[];
  chart: ChartLine[];
  revenue: number;
  revenueIncludingNotPaid: number;
  paidToMikeAmount: number;
  paidToAlexAmount: number;
  totalRevenue: number;
  totalRevenueIncludingNotPaid: number;
  totalPaidToMikeAmount: number;
  totalPaidToAlexAmount: number;
}

export interface Quarter {
  title: string;
  sums: CurrencySum[];
}

export interface CurrencySum {
  currency: string;
  sumPaid: number;
  sumUnpaid: number;
  sumExpenses: number;
  sumPaidUsd: number;
  sumUnpaidUsd: number;
  sumExpensesUsd: number;
}

export interface CustomerDebt {
  customer: string;
  totalAmountDebt: number;
}

export interface ChartLine {
  title: string;
  items: ChartDataItem[];
}

export interface ChartDataItem {
  text: string;
  value: number;
}

export interface Project {
  id: number;
  title: string;
  description?: string;
  date: string;
  po?: string;
  customerId: number;
  customerTitle: string;
  customerGroup?: string;
  statusId: number;
  category?: number;
  statusReason?: string;
  showInTimeTracking: boolean;
  isDayOffProject: boolean;
  transactions: Transaction[];
  projectTags?: ProjectTag[];
}

export interface Customer {
  id: number;
  title: string;
  description?: string;
  date: string;
  website?: string;
  statusId: number;
  type: CustomerType;
  isActive?: boolean;
  isArchive: boolean;
  showInTimeTracking: boolean;
  officialId?: string;
  country?: string;
  longitude?: number;
  latitude?: number;
  group?: string;
  projects: Project[];
  tags: Tag[];
  persons: CustomerPersonDto[];
}

export interface CreateCustomer {
  title: string;
  description?: string;
  date: string;
  website?: string;
  statusId: number;
  type?: CustomerType;
  isArchive: boolean;
  showInTimeTracking: boolean;
  officialId?: string;
  country?: string;
  longitude?: number;
  latitude?: number;
  group?: string;
  tagIds: number[];
}

export interface UpdateCustomer extends CreateCustomer {
  id: number;
}

export interface CustomerFilter {
  statusId?: number;
  type?: CustomerType;
  isArchive?: boolean;
  tagIds?: number[];
  searchText?: string;
  skip?: number;
  take?: number;
}

export interface CreateProject {
  title: string;
  description?: string;
  date: string;
  po?: string;
  customerId: number;
  statusId: number;
  category?: number;
  tags?: string;
  statusReason?: string;
  showInTimeTracking: boolean;
  isDayOffProject: boolean;
  tagIds: number[];
}

export interface UpdateProject extends CreateProject {
  id: number;
}

export interface EmailDto {
  id: number;
  address: string;
}

export interface PhoneDto {
  id: number;
  number: string;
}

export interface SocialLinkDto {
  id: number;
  url: string;
}

export interface Person {
  id: number;
  firstName: string;
  lastName: string;
  middleName?: string;
  notes?: string;
  linkedInUrl?: string;
  contactType: ContactType;
  emails: EmailDto[];
  phones: PhoneDto[];
  socialLinks: SocialLinkDto[];
  customers: CustomerPersonDto[];
  needsReview?: boolean;
  reviewReason?: string;
  importSourceText?: string;
  importSourceUrl?: string;
  importSourceLabel?: string;
  importCompanyName?: string;
  importCompanyWebsite?: string;
  importJobTitle?: string;
  importDepartment?: string;
  importWorkingEmail?: string;
}

export interface CreatePerson {
  firstName: string;
  lastName: string;
  middleName?: string;
  notes?: string;
  linkedInUrl?: string;
  contactType: ContactType;
  emails: EmailDto[];
  phones: PhoneDto[];
  socialLinks: SocialLinkDto[];
  customers: CreateCustomerPersonDto[];
}

export interface UpdatePerson extends CreatePerson {
  id: number;
}

export interface CustomerPersonDto {
  id: number;
  customerId: number;
  customerTitle: string;
  personId: number;
  personFirstName: string;
  personLastName: string;
  isFormer?: boolean;
  jobTitle?: string;
  department?: string;
  workingEmail?: string;
  createdDate: string;
}

export interface CreateCustomerPersonDto {
  customerId: number;
  personId?: number;
  isFormer?: boolean;
  jobTitle?: string;
  department?: string;
  workingEmail?: string;
}

export interface UpdateCustomerPersonDto extends CreateCustomerPersonDto {
  id: number;
}

export enum ContactType {
  Hot = 0,
  Cold = 1
}

export enum Currencies {
  UAH = 1,
  USD = 2,
  RUB = 3,
  EUR = 4,
  SEK = 5
}

export enum TransactionDirections {
  Alex = 0,
  Mike = 1
}

export enum CustomerType {
  Government = 10,
  Education = 11,
  Legal = 12,
  Healthcare = 13,
  Retail = 14,
  Banking = 15,
  Nonprofit = 5,
  Publishers = 17,
  Agriculture = 18,
  Logistics = 19,
  Telecommunications = 20,
  Media = 21,
  Hospitality = 22,
  ITCluster = 23
}

export enum CustomerStatus {
  Active = 1,
  InProgress = 2,
  Completed = 3,
  Inactive = 4
}

export enum ProjectStatus {
  Lead = 1,
  Opportunity = 2,
  Active = 3,
  Rejected = 4,
  Completed = 5,
  Support = 6,
  OnHold = 7,
  Cancelled = 8
}

export interface Tag {
  id: number;
  name: string;
}

export interface CreateTag {
  name: string;
}

export interface UpdateTag extends CreateTag {
  id: number;
}

export interface ProjectProjectTag {
  id: number;
  projectId: number;
  projectTagId: number;
  projectTag?: ProjectTag;
}

export interface ProjectTag {
  id: number;
  name: string;
}

export interface CreateProjectTag {
  name: string;
}

export interface UpdateProjectTag extends CreateProjectTag {
  id: number;
}

export interface StatisticView {
  data: Quarter;
  chatItems: ChartDataItem[];
}

export interface PaginationRequest {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface PaginatedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface Activity {
  id: number;
  title: string;
  createdDate: string;
  /** Server-owned UTC creation instant (DEV-35); null/absent means Unknown. */
  createdAtUtc?: string | null;
  referenceType: string;
  referenceId: number;
}

export interface CreateActivity {
  title: string;
  createdDate: string;
  referenceType: string;
  referenceId: number;
}

export interface UpdateActivity extends CreateActivity {
  id: number;
}

export interface ActivityLog {
  id: number;
  entityType: string;
  entityId: number;
  actionType: string;
  entityTitle: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  summary?: string;
  createdDate: string;
}

export interface ActivityLogFilter {
  entityType?: string;
  entityId?: number;
  take?: number;
}

export interface ProjectPerson {
  id: number;
  projectId: number;
  projectTitle: string;
  personId: number;
  personFirstName: string;
  personLastName: string;
  role?: string;
}

export interface CreateProjectPerson {
  projectId: number;
  personId: number;
  role?: string;
}

export interface UpdateProjectPerson extends CreateProjectPerson {
  id: number;
}

export interface CustomerPerson {
  id: number;
  customerId: number;
  customerTitle: string;
  personId: number;
  personFirstName: string;
  personLastName: string;
  isFormer?: boolean;
  jobTitle?: string;
  department?: string;
  workingEmail?: string;
  createdDate: string;
}
